'use client';

import {
  Fragment,
  createElement,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ClipboardEvent,
  type CSSProperties,
  type ElementType,
  type FocusEvent,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import {
  useEditMode,
  type RichTextAlign,
  type RichTextFont,
  type RichTextListKind,
  type RichTextMark,
  type RichTextSegment,
  type RichTextValue,
} from './context';
import { createPortal } from 'react-dom';
import { getByPath } from './get-by-path';

type Props = {
  path: string;
  as?: ElementType;
  multiline?: boolean;
  richText?: boolean;
  className?: string;
  placeholder?: string;
  style?: CSSProperties;
  children: ReactNode;
};

const FONT_OPTIONS: readonly { value: RichTextFont; label: string }[] = [
  { value: 'inherit', label: 'Fonte' },
  { value: 'display', label: 'Display' },
  { value: 'sans', label: 'Sans' },
  { value: 'serif', label: 'Serif' },
  { value: 'mono', label: 'Mono' },
  { value: 'cormorant', label: 'Cormorant' },
  { value: 'libre', label: 'Libre Baskerville' },
  { value: 'greatVibes', label: 'Great Vibes' },
  { value: 'inter', label: 'Inter' },
  { value: 'georgia', label: 'Georgia' },
  { value: 'times', label: 'Times' },
  { value: 'bricolage', label: 'Bricolage' },
  { value: 'geist', label: 'Geist' },
  { value: 'newsreader', label: 'Newsreader' },
  { value: 'jetbrains', label: 'JetBrains Mono' },
];

const FONT_CSS: Record<Exclude<RichTextFont, 'inherit'>, string> = {
  display: 'var(--font-display), "Bricolage Grotesque", "Cormorant Garamond", Georgia, serif',
  sans: 'var(--font-body), Geist, Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  serif: 'var(--font-serif), Newsreader, "Cormorant Garamond", Georgia, "Times New Roman", serif',
  mono: 'var(--font-mono), "JetBrains Mono", ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", monospace',
  cormorant: '"Cormorant Garamond", Georgia, "Times New Roman", serif',
  libre: '"Libre Baskerville", Georgia, "Times New Roman", serif',
  greatVibes: '"Great Vibes", cursive',
  inter: 'Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  georgia: 'Georgia, "Times New Roman", serif',
  times: '"Times New Roman", Times, serif',
  bricolage: 'var(--font-display), "Bricolage Grotesque", ui-sans-serif, system-ui, sans-serif',
  geist: 'var(--font-body), Geist, ui-sans-serif, system-ui, sans-serif',
  newsreader: 'var(--font-serif), Newsreader, Georgia, "Times New Roman", serif',
  jetbrains: 'var(--font-mono), "JetBrains Mono", ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
};

const ALIGN_OPTIONS: readonly RichTextAlign[] = ['left', 'center', 'right'];
const DEFAULT_TEXT_COLOR = '#f5eee0';
const RICH_TEXT_LINK_CLASS = 'locusly-rich-text-link';
const MIN_FONT_SIZE = 8;
const MAX_FONT_SIZE = 160;

const RICH_TEXT_LINK_STYLE: CSSProperties = {
  color: '#2563eb',
  textDecorationLine: 'underline',
  textDecorationThickness: '0.08em',
  textUnderlineOffset: '0.16em',
};

const RICH_TEXT_LINK_CSS = `
  .${RICH_TEXT_LINK_CLASS} {
    color: #2563eb;
    text-decoration-line: underline;
    text-decoration-thickness: 0.08em;
    text-underline-offset: 0.16em;
  }
`;

function isRichTextValue(value: unknown): value is RichTextValue {
  return (
    Boolean(value) &&
    typeof value === 'object' &&
    !Array.isArray(value) &&
    (value as { type?: unknown }).type === 'richText' &&
    Array.isArray((value as { content?: unknown }).content)
  );
}

function normalizeMarks(marks: readonly RichTextMark[]): RichTextMark[] | undefined {
  const order: readonly RichTextMark[] = ['bold', 'italic', 'underline'];
  const normalized = order.filter((mark) => marks.includes(mark));
  return normalized.length > 0 ? normalized : undefined;
}

function sameMarks(a: readonly RichTextMark[] | undefined, b: readonly RichTextMark[] | undefined) {
  const left = normalizeMarks(a ?? []) ?? [];
  const right = normalizeMarks(b ?? []) ?? [];
  return left.length === right.length && left.every((mark, index) => mark === right[index]);
}

function normalizeListKind(value: unknown): RichTextListKind | undefined {
  return value === 'bullet' || value === 'numbered' ? value : undefined;
}

function sameSegmentFormat(
  a: RichTextSegment,
  marks: readonly RichTextMark[] | undefined,
  link?: string,
  list?: RichTextListKind,
) {
  return sameMarks(a.marks, marks) && (a.link ?? '') === (link ?? '') && a.list === list;
}

function normalizeSegments(segments: readonly RichTextSegment[]): RichTextSegment[] {
  const result: RichTextSegment[] = [];
  for (const segment of segments) {
    if (!segment.text) continue;
    const marks = normalizeMarks(segment.marks ?? []);
    const link = typeof segment.link === 'string' && segment.link.trim() ? segment.link.trim() : undefined;
    const list = normalizeListKind(segment.list);
    const previous = result[result.length - 1];
    if (previous && sameSegmentFormat(previous, marks, link, list)) {
      previous.text += segment.text;
    } else {
      result.push({
        text: segment.text,
        ...(marks ? { marks } : {}),
        ...(link ? { link } : {}),
        ...(list ? { list } : {}),
      });
    }
  }
  return result;
}

function plainTextFromSegments(segments: readonly RichTextSegment[]): string {
  return segments.map((segment) => segment.text).join('');
}

function richTextFromText(text: string): RichTextValue {
  return { type: 'richText', content: text ? [{ text }] : [] };
}

function cloneStoredValue(value: unknown): unknown {
  if (value === undefined || value === null) return value;
  if (typeof structuredClone === 'function') return structuredClone(value);
  return JSON.parse(JSON.stringify(value));
}

function sameStoredValue(a: unknown, b: unknown): boolean {
  return JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
}

function formatAtOffset(
  segments: readonly RichTextSegment[],
  offset: number,
): Pick<RichTextSegment, 'marks' | 'link' | 'list'> {
  let position = 0;
  let previous: RichTextSegment | undefined;
  for (const segment of segments) {
    const nextPosition = position + segment.text.length;
    if (offset <= nextPosition) {
      return {
        marks: normalizeMarks(segment.marks ?? []),
        link: segment.link,
        list: normalizeListKind(segment.list),
      };
    }
    previous = segment;
    position = nextPosition;
  }

  return {
    marks: normalizeMarks(previous?.marks ?? []),
    link: previous?.link,
    list: normalizeListKind(previous?.list),
  };
}

function segmentSlice(
  segment: RichTextSegment,
  start: number,
  end: number,
  link = segment.link,
  list = segment.list,
): RichTextSegment {
  return {
    text: segment.text.slice(start, end),
    marks: normalizeMarks(segment.marks ?? []),
    ...(link ? { link } : {}),
    ...(list ? { list } : {}),
  };
}

function applyLinkToSegments(
  segments: readonly RichTextSegment[],
  selection: TextSelectionSnapshot,
  href: string | undefined,
  replacementText?: string,
): RichTextSegment[] {
  const contentLength = plainTextFromSegments(segments).length;
  const start = clamp(selection.start, 0, contentLength);
  const end = clamp(selection.end, 0, contentLength);
  const isCollapsed = start === end;
  const replacement = replacementText?.trim();

  if (isCollapsed && !href && !replacement) return normalizeSegments(segments);
  if (isCollapsed && href && !replacement) return normalizeSegments(segments);

  const result: RichTextSegment[] = [];
  let insertedReplacement = false;
  let position = 0;
  const insertionFormat = formatAtOffset(segments, start);

  const pushReplacement = () => {
    if (!href || !replacement || insertedReplacement) return;
    result.push({
      text: replacement,
      marks: normalizeMarks(insertionFormat.marks ?? []),
      link: href,
      ...(insertionFormat.list ? { list: insertionFormat.list } : {}),
    });
    insertedReplacement = true;
  };

  if (isCollapsed) {
    for (const segment of segments) {
      const segmentStart = position;
      const segmentEnd = position + segment.text.length;
      position = segmentEnd;

      if (!insertedReplacement && start >= segmentStart && start <= segmentEnd) {
        const localStart = start - segmentStart;
        if (localStart > 0) result.push(segmentSlice(segment, 0, localStart));
        pushReplacement();
        if (localStart < segment.text.length) result.push(segmentSlice(segment, localStart, segment.text.length));
        continue;
      }

      if (!insertedReplacement && start <= segmentStart) pushReplacement();
      result.push(segment);
    }

    if (!insertedReplacement) pushReplacement();
    return normalizeSegments(result);
  }

  for (const segment of segments) {
    const segmentStart = position;
    const segmentEnd = position + segment.text.length;
    position = segmentEnd;

    if (segmentEnd <= start || segmentStart >= end) {
      if (!insertedReplacement && replacement && segmentStart >= end) pushReplacement();
      result.push(segment);
      continue;
    }

    const localStart = Math.max(start - segmentStart, 0);
    const localEnd = Math.min(end - segmentStart, segment.text.length);

    if (localStart > 0) result.push(segmentSlice(segment, 0, localStart));

    if (replacement) {
      pushReplacement();
    } else {
      result.push(segmentSlice(segment, localStart, localEnd, href));
    }

    if (localEnd < segment.text.length) result.push(segmentSlice(segment, localEnd, segment.text.length));
  }

  if (!insertedReplacement) pushReplacement();
  return normalizeSegments(result);
}

function replaceSelectionWithSegments(
  segments: readonly RichTextSegment[],
  selection: TextSelectionSnapshot,
  replacementSegments: readonly RichTextSegment[],
): RichTextSegment[] {
  const contentLength = plainTextFromSegments(segments).length;
  const start = clamp(selection.start, 0, contentLength);
  const end = clamp(selection.end, 0, contentLength);
  const result: RichTextSegment[] = [];
  let inserted = false;
  let position = 0;

  const pushReplacement = () => {
    if (inserted) return;
    result.push(...replacementSegments);
    inserted = true;
  };

  for (const segment of segments) {
    const segmentStart = position;
    const segmentEnd = position + segment.text.length;
    position = segmentEnd;

    if (start === end) {
      if (!inserted && start >= segmentStart && start <= segmentEnd) {
        const localStart = start - segmentStart;
        if (localStart > 0) result.push(segmentSlice(segment, 0, localStart));
        pushReplacement();
        if (localStart < segment.text.length) result.push(segmentSlice(segment, localStart, segment.text.length));
        continue;
      }
      if (!inserted && start <= segmentStart) pushReplacement();
      result.push(segment);
      continue;
    }

    if (segmentEnd <= start || segmentStart >= end) {
      if (!inserted && segmentStart >= end) pushReplacement();
      result.push(segment);
      continue;
    }

    const localStart = Math.max(start - segmentStart, 0);
    const localEnd = Math.min(end - segmentStart, segment.text.length);
    if (localStart > 0) result.push(segmentSlice(segment, 0, localStart));
    pushReplacement();
    if (localEnd < segment.text.length) result.push(segmentSlice(segment, localEnd, segment.text.length));
  }

  if (!inserted) pushReplacement();
  return normalizeSegments(result);
}

function applyListToSegments(
  segments: readonly RichTextSegment[],
  selection: TextSelectionSnapshot,
  kind: RichTextListKind,
): RichTextSegment[] {
  const text = plainTextFromSegments(segments);
  const contentLength = text.length;
  const start = clamp(selection.start, 0, contentLength);
  const end = clamp(selection.end, 0, contentLength);
  const selectedText = start === end ? 'Item' : text.slice(start, end);
  const baseFormat = formatAtOffset(segments, start);
  const replacementSegments = selectedText
    .replace(/\r\n?/g, '\n')
    .split('\n')
    .map(stripListMarker)
    .filter((line) => line.trim().length > 0)
    .map((line) => ({
      text: `${line}\n`,
      marks: normalizeMarks(baseFormat.marks ?? []),
      ...(baseFormat.link ? { link: baseFormat.link } : {}),
      list: kind,
    }));

  return replaceSelectionWithSegments(
    segments,
    selection,
    replacementSegments.length > 0 ? replacementSegments : [{ text: 'Item\n', list: kind }],
  );
}

function escapeHtml(value: string): string {
  return value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;');
}

function escapeAttribute(value: string): string {
  return escapeHtml(value).replaceAll("'", '&#39;');
}

function segmentInlineHtml(segment: RichTextSegment, text = segment.text): string {
  const escapedText = escapeHtml(text).replaceAll('\n', '<br>');
  const marks = normalizeMarks(segment.marks ?? []) ?? [];
  const marked = marks.reduce((html, mark) => {
    if (mark === 'bold') return `<strong>${html}</strong>`;
    if (mark === 'italic') return `<em>${html}</em>`;
    return `<u>${html}</u>`;
  }, escapedText);
  if (!segment.link) return marked;
  return `<a class="${RICH_TEXT_LINK_CLASS}" href="${escapeAttribute(segment.link)}">${marked}</a>`;
}

function listItemsToHtml(kind: RichTextListKind, items: string[]): string {
  const tag = kind === 'numbered' ? 'ol' : 'ul';
  return `<${tag}>${items.map((item) => `<li>${item || '<br>'}</li>`).join('')}</${tag}>`;
}

function segmentsToHtml(segments: readonly RichTextSegment[]): string {
  const html: string[] = [];
  let activeList: RichTextListKind | undefined;
  let listItems: string[] = [];

  const flushList = () => {
    if (!activeList) return;
    html.push(listItemsToHtml(activeList, listItems));
    activeList = undefined;
    listItems = [];
  };

  for (const segment of segments) {
    const list = normalizeListKind(segment.list);
    if (!list) {
      flushList();
      html.push(segmentInlineHtml(segment));
      continue;
    }

    if (activeList && activeList !== list) flushList();
    activeList = list;
    const parts = segment.text.replace(/\n$/, '').split('\n');
    parts.forEach((part, index) => {
      if (index > 0) listItems.push('');
      if (!part) return;
      if (listItems.length === 0) listItems.push('');
      listItems[listItems.length - 1] += segmentInlineHtml(segment, part);
    });
  }

  flushList();
  return html.join('');
}

function normalizeFontSize(value: unknown): number | undefined {
  if (typeof value !== 'number' || !Number.isFinite(value)) return undefined;
  const rounded = Math.round(value);
  if (rounded < MIN_FONT_SIZE || rounded > MAX_FONT_SIZE) return undefined;
  return rounded;
}

function normalizeColor(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return /^#[0-9a-f]{6}$/i.test(trimmed) ? trimmed : undefined;
}

function normalizeRichTextStyle(style: RichTextValue['style']): RichTextValue['style'] | undefined {
  if (!style) return undefined;
  const next: NonNullable<RichTextValue['style']> = {};
  if (style.fontFamily && style.fontFamily !== 'inherit') next.fontFamily = style.fontFamily;
  const fontSize = normalizeFontSize(style.fontSize);
  if (fontSize) next.fontSize = fontSize;
  if (style.textAlign && style.textAlign !== 'left') next.textAlign = style.textAlign;
  const color = normalizeColor(style.color);
  if (color) next.color = color;
  return Object.keys(next).length > 0 ? next : undefined;
}

function hasRichFormatting(value: RichTextValue): boolean {
  const style = normalizeRichTextStyle(value.style);
  return (
    Boolean(style) ||
    value.content.some((segment) => (segment.marks?.length ?? 0) > 0 || Boolean(segment.link) || Boolean(segment.list))
  );
}

function coerceRichText(value: unknown, fallback: string): RichTextValue {
  if (isRichTextValue(value)) {
    return {
      type: 'richText',
      content: normalizeSegments(
        value.content
          .filter((segment) => segment && typeof segment.text === 'string')
          .map((segment) => ({
            text: segment.text,
            marks: normalizeMarks(segment.marks ?? []),
            link: typeof segment.link === 'string' ? segment.link : undefined,
            list: normalizeListKind(segment.list),
          })),
      ),
      style: normalizeRichTextStyle(value.style),
    };
  }
  return richTextFromText(typeof value === 'string' ? value : fallback);
}

function toStoredTextValue(value: RichTextValue, previous: unknown): string | RichTextValue {
  const normalized: RichTextValue = {
    type: 'richText',
    content: normalizeSegments(value.content),
    style: normalizeRichTextStyle(value.style),
  };
  if (!isRichTextValue(previous) && !hasRichFormatting(normalized)) {
    return plainTextFromSegments(normalized.content);
  }
  return normalized;
}

function styleForRichText(base: CSSProperties | undefined, richText: RichTextValue): CSSProperties | undefined {
  const richStyle = normalizeRichTextStyle(richText.style);
  if (!richStyle) return base;
  const font =
    richStyle.fontFamily && richStyle.fontFamily !== 'inherit' ? richStyle.fontFamily : undefined;
  return {
    ...base,
    ...(font ? { fontFamily: FONT_CSS[font] } : {}),
    ...(richStyle.fontSize ? { fontSize: `${richStyle.fontSize}px` } : {}),
    ...(richStyle.textAlign ? { textAlign: richStyle.textAlign } : {}),
    ...(richStyle.color ? { color: richStyle.color } : {}),
  };
}

function appendSegment(
  target: RichTextSegment[],
  text: string,
  marks: readonly RichTextMark[],
  link?: string,
  list?: RichTextListKind,
) {
  if (!text) return;
  target.push({
    text,
    marks: normalizeMarks(marks),
    ...(link ? { link } : {}),
    ...(list ? { list } : {}),
  });
}

function marksForElement(element: Element, inherited: readonly RichTextMark[]): RichTextMark[] {
  const marks = new Set<RichTextMark>(inherited);
  const tag = element.tagName.toLowerCase();
  const style = (element as HTMLElement).style;

  if (tag === 'strong' || tag === 'b') marks.add('bold');
  if (tag === 'em' || tag === 'i') marks.add('italic');
  if (tag === 'u') marks.add('underline');
  if (style.fontWeight === 'bold' || Number(style.fontWeight) >= 600) marks.add('bold');
  if (style.fontStyle === 'italic') marks.add('italic');
  if (style.textDecorationLine.includes('underline') || style.textDecoration.includes('underline')) {
    marks.add('underline');
  }

  return Array.from(marks);
}

function parseEditableNode(
  node: Node,
  marks: readonly RichTextMark[],
  link: string | undefined,
  list: RichTextListKind | undefined,
  segments: RichTextSegment[],
) {
  if (node.nodeType === Node.TEXT_NODE) {
    appendSegment(segments, node.textContent ?? '', marks, link, list);
    return;
  }
  if (node.nodeType !== Node.ELEMENT_NODE) return;

  const element = node as Element;
  const tag = element.tagName.toLowerCase();
  if (tag === 'br') {
    appendSegment(segments, '\n', marks, link, list);
    return;
  }

  const nextMarks = marksForElement(element, marks);
  const nextLink =
    tag === 'a' && element instanceof HTMLAnchorElement
      ? element.getAttribute('href') ?? undefined
      : link;
  const nextList =
    tag === 'li'
      ? element.parentElement?.tagName.toLowerCase() === 'ol'
        ? 'numbered'
        : 'bullet'
      : list;
  const isBlock = tag === 'div' || tag === 'p';
  const hadContent = segments.length > 0 && !plainTextFromSegments(segments).endsWith('\n');
  if (isBlock && hadContent) appendSegment(segments, '\n', marks, link, list);
  element.childNodes.forEach((child) => parseEditableNode(child, nextMarks, nextLink, nextList, segments));
  if (tag === 'li') appendSegment(segments, '\n', nextMarks, nextLink, nextList);
}

function parseEditableElement(element: HTMLElement): RichTextSegment[] {
  const segments: RichTextSegment[] = [];
  element.childNodes.forEach((child) => parseEditableNode(child, [], undefined, undefined, segments));
  return normalizeSegments(segments);
}

function renderSegmentText(text: string, keyPrefix: string) {
  const parts = text.split('\n');
  return parts.flatMap((part, index) => {
    const nodes: ReactNode[] = [];
    if (index > 0) nodes.push(<br key={`${keyPrefix}-br-${index}`} />);
    if (part) nodes.push(part);
    return nodes;
  });
}

function renderInlineSegment(segment: RichTextSegment, index: string | number, text = segment.text): ReactNode {
    const marks = normalizeMarks(segment.marks ?? []) ?? [];
    const segmentStyle: CSSProperties = {
      fontWeight: marks.includes('bold') ? 700 : undefined,
      fontStyle: marks.includes('italic') ? 'italic' : undefined,
      textDecorationLine: marks.includes('underline') ? 'underline' : undefined,
    };
    const hasStyle = Object.values(segmentStyle).some(Boolean);
    const content = (
      <span style={hasStyle ? segmentStyle : undefined}>
        {renderSegmentText(text, `text-${index}`)}
      </span>
    );
    if (segment.link) {
      return (
        <a key={index} href={segment.link} className={RICH_TEXT_LINK_CLASS} style={RICH_TEXT_LINK_STYLE}>
          {content}
        </a>
      );
    }
    return (
      <Fragment key={index}>
        {content}
      </Fragment>
    );
}

function renderRichTextContent(value: RichTextValue): ReactNode {
  const nodes: ReactNode[] = [];
  let index = 0;

  while (index < value.content.length) {
    const segment = value.content[index]!;
    const list = normalizeListKind(segment.list);
    if (!list) {
      nodes.push(renderInlineSegment(segment, index));
      index += 1;
      continue;
    }

    const items: ReactNode[][] = [[]];
    while (index < value.content.length && normalizeListKind(value.content[index]!.list) === list) {
      const listSegment = value.content[index]!;
      const parts = listSegment.text.replace(/\n$/, '').split('\n');
      parts.forEach((part, partIndex) => {
        if (partIndex > 0) items.push([]);
        if (!part) return;
        items[items.length - 1]!.push(renderInlineSegment(listSegment, `${index}-${partIndex}`, part));
      });
      index += 1;
    }

    const children = items
      .filter((item) => item.length > 0)
      .map((item, itemIndex) => <li key={itemIndex}>{item}</li>);
    nodes.push(
      list === 'numbered' ? (
        <ol key={`list-${index}`}>{children}</ol>
      ) : (
        <ul key={`list-${index}`}>{children}</ul>
      ),
    );
  }

  return nodes;
}

type SelectionGeometry = {
  frame: CSSProperties;
  toolbar: CSSProperties;
  arrow: CSSProperties;
};

type TextSelectionSnapshot = {
  start: number;
  end: number;
};

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function detectFontFromComputedStyle(fontFamily: string | undefined): RichTextFont {
  if (!fontFamily) return 'inherit';
  const cf = fontFamily.toLowerCase();
  if (cf.includes('libre baskerville')) return 'libre';
  if (cf.includes('great vibes')) return 'greatVibes';
  if (cf.includes('jetbrains')) return 'jetbrains';
  if (cf.includes('cormorant')) return 'cormorant';
  if (cf.includes('newsreader')) return 'newsreader';
  if (cf.includes('bricolage')) return 'bricolage';
  if (cf.includes('geist')) return 'geist';
  if (cf.includes('inter')) return 'inter';
  if (cf.includes('georgia')) return 'georgia';
  if (cf.includes('times')) return 'times';
  return 'inherit';
}

function rgbToHex(value: string | undefined): string | undefined {
  if (!value) return undefined;
  if (/^#[0-9a-f]{6}$/i.test(value)) return value;
  const match = value.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/i);
  if (!match) return undefined;
  return `#${[match[1], match[2], match[3]]
    .map((part) => clamp(Number(part), 0, 255).toString(16).padStart(2, '0'))
    .join('')}`;
}

function closestAnchor(node: Node | null, root: HTMLElement): HTMLAnchorElement | null {
  let current: Node | null = node;
  while (current && current !== root) {
    if (current instanceof HTMLAnchorElement) return current;
    current = current.parentNode;
  }
  return null;
}

function isSelectionInside(selection: Selection | null, root: HTMLElement): boolean {
  if (!selection || selection.rangeCount === 0) return false;
  const range = selection.getRangeAt(0);
  return isRangeInside(range, root);
}

function isRangeInside(range: Range, root: HTMLElement): boolean {
  return (
    root.contains(range.commonAncestorContainer) &&
    root.contains(range.startContainer) &&
    root.contains(range.endContainer)
  );
}

function selectElementContents(element: HTMLElement): Range | null {
  const selection = document.getSelection();
  if (!selection) return null;
  const range = document.createRange();
  range.selectNodeContents(element);
  selection.removeAllRanges();
  selection.addRange(range);
  return range.cloneRange();
}

function getNodeTextLength(node: Node): number {
  if (node.nodeType === Node.TEXT_NODE) return node.textContent?.length ?? 0;
  if (node.nodeType === Node.ELEMENT_NODE && (node as Element).tagName.toLowerCase() === 'br') {
    return 1;
  }

  let length = 0;
  node.childNodes.forEach((child) => {
    length += getNodeTextLength(child);
  });
  return length;
}

function getTextOffset(root: HTMLElement, container: Node, offset: number): number | null {
  let total = 0;
  let found = false;

  const walk = (node: Node): boolean => {
    if (node === container) {
      if (node.nodeType === Node.TEXT_NODE) {
        total += clamp(offset, 0, node.textContent?.length ?? 0);
      } else {
        const children = Array.from(node.childNodes);
        for (const child of children.slice(0, clamp(offset, 0, children.length))) {
          total += getNodeTextLength(child);
        }
      }
      found = true;
      return true;
    }

    if (node.nodeType === Node.TEXT_NODE) {
      total += node.textContent?.length ?? 0;
      return false;
    }

    if (node.nodeType === Node.ELEMENT_NODE && (node as Element).tagName.toLowerCase() === 'br') {
      total += 1;
      return false;
    }

    for (const child of Array.from(node.childNodes)) {
      if (walk(child)) return true;
    }
    return false;
  };

  walk(root);
  return found ? total : null;
}

function snapshotSelectionRange(root: HTMLElement, range: Range): TextSelectionSnapshot | null {
  if (!isRangeInside(range, root)) return null;
  const start = getTextOffset(root, range.startContainer, range.startOffset);
  const end = getTextOffset(root, range.endContainer, range.endOffset);
  if (start == null || end == null) return null;
  return { start: Math.min(start, end), end: Math.max(start, end) };
}

function getDomPositionAtTextOffset(root: HTMLElement, textOffset: number): { node: Node; offset: number } {
  const targetOffset = clamp(textOffset, 0, getNodeTextLength(root));
  let remaining = targetOffset;

  const walk = (node: Node): { node: Node; offset: number } | null => {
    if (node.nodeType === Node.TEXT_NODE) {
      const length = node.textContent?.length ?? 0;
      if (remaining <= length) return { node, offset: remaining };
      remaining -= length;
      return null;
    }

    if (node.nodeType === Node.ELEMENT_NODE && (node as Element).tagName.toLowerCase() === 'br') {
      const parent = node.parentNode;
      if (parent && remaining <= 1) {
        const index = Array.prototype.indexOf.call(parent.childNodes, node);
        return { node: parent, offset: remaining === 0 ? index : index + 1 };
      }
      remaining -= 1;
      return null;
    }

    for (const child of Array.from(node.childNodes)) {
      const position = walk(child);
      if (position) return position;
    }
    return null;
  };

  return walk(root) ?? { node: root, offset: root.childNodes.length };
}

function rangeFromSelectionSnapshot(root: HTMLElement, snapshot: TextSelectionSnapshot): Range {
  const range = document.createRange();
  const start = getDomPositionAtTextOffset(root, snapshot.start);
  const end = getDomPositionAtTextOffset(root, snapshot.end);
  range.setStart(start.node, start.offset);
  range.setEnd(end.node, end.offset);
  return range;
}

function normalizePastedText(text: string, multiline: boolean): string {
  const normalized = text.replace(/\r\n?/g, '\n');
  return multiline ? normalized : normalized.replace(/\n+/g, ' ');
}

function stripListMarker(line: string): string {
  return line.trim().replace(/^(?:(?:[-*]|\u2022)\s+|\d+[.)]\s+)/, '');
}

function insertPlainTextAtSelection(root: HTMLElement, text: string): boolean {
  const selection = document.getSelection();
  if (!selection) return false;

  let range = selection.rangeCount > 0 ? selection.getRangeAt(0) : null;
  if (!range || !isRangeInside(range, root)) {
    range = document.createRange();
    range.selectNodeContents(root);
    range.collapse(false);
  }

  range.deleteContents();

  const fragment = document.createDocumentFragment();
  for (const [index, part] of text.split('\n').entries()) {
    if (index > 0) fragment.appendChild(document.createElement('br'));
    if (part) fragment.appendChild(document.createTextNode(part));
  }

  const lastNode = fragment.lastChild;
  range.insertNode(fragment);

  const nextRange = document.createRange();
  if (lastNode) {
    nextRange.setStartAfter(lastNode);
  } else {
    nextRange.setStart(range.endContainer, range.endOffset);
  }
  nextRange.collapse(true);
  selection.removeAllRanges();
  selection.addRange(nextRange);
  return true;
}

function normalizeHref(value: string): string | undefined {
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  if (/^(https?:|mailto:|tel:|\/|#)/i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
}

const toolbarDividerStyle: CSSProperties = {
  width: 1,
  height: 22,
  margin: '0 3px',
  background: 'rgba(255, 255, 255, 0.12)',
};

const selectionHandlePositions: readonly CSSProperties[] = [
  { left: -5, top: -5 },
  { right: -5, top: -5 },
  { right: -5, bottom: -5 },
  { left: -5, bottom: -5 },
];

function toolbarButtonStyle(font?: 'bold' | 'italic', active = false): CSSProperties {
  return {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 32,
    height: 32,
    flex: '0 0 32px',
    border: 0,
    borderRadius: 8,
    background: active ? 'rgba(234, 199, 125, 0.22)' : 'rgba(255, 255, 255, 0.08)',
    color: '#fff8e8',
    cursor: 'pointer',
    fontSize: 14,
    fontWeight: font === 'bold' ? 800 : 650,
    fontStyle: font === 'italic' ? 'italic' : undefined,
    lineHeight: 1,
  };
}

export function EditableText({
  path,
  as = 'span',
  multiline = false,
  richText: richTextEnabled = true,
  className,
  placeholder,
  style,
  children,
}: Props) {
  const { editing, values, setByPath, selectedPath, setSelected } =
    useEditMode();
  const ref = useRef<HTMLElement | null>(null);
  const selectionRangeRef = useRef<Range | null>(null);
  const selectionSnapshotRef = useRef<TextSelectionSnapshot | null>(null);
  const undoStackRef = useRef<unknown[]>([]);
  const redoStackRef = useRef<unknown[]>([]);
  const [geometry, setGeometry] = useState<SelectionGeometry | null>(null);
  const [linkEditorOpen, setLinkEditorOpen] = useState(false);
  const [linkHref, setLinkHref] = useState('');
  const [linkText, setLinkText] = useState('');

  // Live content (Convex draft/published values) wins; `children` is the
  // client's static default/fallback.
  const fallback =
    typeof children === 'string'
      ? children
      : children == null
        ? ''
        : String(children);
  const resolved = getByPath(values, path);
  const richText = coerceRichText(resolved, fallback);
  const text = plainTextFromSegments(richText.content);
  const effectiveStyle = richTextEnabled ? styleForRichText(style, richText) : style;
  const isSelected = selectedPath === path;

  function setTextValue(next: unknown) {
    if (!sameStoredValue(resolved, next)) {
      undoStackRef.current.push(cloneStoredValue(resolved));
      redoStackRef.current = [];
    }
    setByPath(path, next);
  }

  function applyHistory(direction: 'undo' | 'redo') {
    const source = direction === 'undo' ? undoStackRef.current : redoStackRef.current;
    const target = direction === 'undo' ? redoStackRef.current : undoStackRef.current;
    if (source.length === 0) return false;
    const next = source.pop();
    target.push(cloneStoredValue(resolved));
    setByPath(path, next);
    selectionRangeRef.current = null;
    selectionSnapshotRef.current = null;
    window.requestAnimationFrame(updateSelectionGeometry);
    return true;
  }

  const updateSelectionGeometry = useCallback(() => {
    const el = ref.current;
    if (!el || typeof window === 'undefined') return;
    const rect = el.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return;

    const viewportWidth = window.innerWidth;
    const toolbarWidth = Math.min(720, Math.max(280, viewportWidth - 16));
    const toolbarHalf = toolbarWidth / 2;
    const naturalCenter = rect.left + rect.width / 2;
    const toolbarCenter =
      viewportWidth <= toolbarWidth + 16
        ? viewportWidth / 2
        : clamp(naturalCenter, 8 + toolbarHalf, viewportWidth - 8 - toolbarHalf);
    const estimatedToolbarHeight = linkEditorOpen ? 86 : 44;
    const toolbarGap = 10;
    const showBelow = rect.top < estimatedToolbarHeight + toolbarGap + 8;
    const preferredToolbarTop = showBelow
      ? rect.bottom + toolbarGap
      : rect.top - estimatedToolbarHeight - toolbarGap;
    const toolbarTop = clamp(
      preferredToolbarTop,
      8,
      Math.max(8, window.innerHeight - estimatedToolbarHeight - 8),
    );

    setGeometry({
      frame: {
        position: 'fixed',
        left: rect.left - 7,
        top: rect.top - 7,
        width: rect.width + 14,
        height: rect.height + 14,
      },
      toolbar: {
        position: 'fixed',
        left: toolbarCenter,
        top: toolbarTop,
        width: toolbarWidth,
        transform: 'translateX(-50%)',
      },
      arrow: {
        position: 'absolute',
        left: clamp(naturalCenter - toolbarCenter + toolbarHalf, 18, toolbarWidth - 18),
        [showBelow ? 'top' : 'bottom']: -4,
        transform: 'translateX(-50%) rotate(45deg)',
      },
    });
  }, [linkEditorOpen]);

  useLayoutEffect(() => {
    if (!editing) return;
    const el = ref.current;
    if (!el) return;
    if (document.activeElement === el) return;
    if (linkEditorOpen) return;
    if (!richTextEnabled) {
      if (el.innerText !== text) el.innerText = text;
      return;
    }
    const html = segmentsToHtml(richText.content);
    if (el.innerHTML !== html) el.innerHTML = html;
  }, [editing, linkEditorOpen, richText.content, richTextEnabled, text]);

  useEffect(() => {
    if (!editing || !isSelected) {
      setGeometry(null);
      return undefined;
    }

    let frame = 0;
    const scheduleUpdate = () => {
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(updateSelectionGeometry);
    };

    updateSelectionGeometry();
    window.addEventListener('resize', scheduleUpdate);
    window.addEventListener('scroll', scheduleUpdate, true);

    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener('resize', scheduleUpdate);
      window.removeEventListener('scroll', scheduleUpdate, true);
    };
  }, [editing, isSelected, updateSelectionGeometry]);

  useEffect(() => {
    if (!editing || !isSelected) return undefined;

    const handleDocumentKeyDown = (event: globalThis.KeyboardEvent) => {
      const primaryShortcut = (event.ctrlKey || event.metaKey) && !event.altKey;
      if (!primaryShortcut) return;
      const shortcutKey = event.key.toLowerCase();
      if (shortcutKey === 'z' || shortcutKey === 'y') {
        const direction = shortcutKey === 'y' || event.shiftKey ? 'redo' : 'undo';
        if (applyHistory(direction)) event.preventDefault();
        return;
      }
      if (shortcutKey !== 'a') return;
      const target = event.target;
      if (
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target instanceof HTMLSelectElement
      ) {
        return;
      }

      event.preventDefault();
      selectWholeEditor();
    };

    document.addEventListener('keydown', handleDocumentKeyDown, true);
    return () => document.removeEventListener('keydown', handleDocumentKeyDown, true);
  }, [editing, isSelected, resolved]);

  if (!editing) {
    const renderedText =
      richTextEnabled && isRichTextValue(resolved)
        ? renderRichTextContent(richText)
        : renderSegmentText(text, `plain-${path}`);
    return createElement(
      as,
      { className, style: effectiveStyle },
      renderedText,
    );
  }

  const editClass = [
    'rounded-sm outline-none cursor-text',
    !text ? 'min-h-[1em] min-w-[2ch]' : '',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ');

  function commitFromElement(forceRich = false) {
    const el = ref.current;
    if (!el) return;
    if (!richTextEnabled) {
      setTextValue(el.innerText);
      return;
    }
    const nextRich: RichTextValue = {
      type: 'richText',
      content: parseEditableElement(el),
      style: richText.style,
    };
    const nextStored = forceRich ? nextRich : toStoredTextValue(nextRich, resolved);
    setTextValue(nextStored);
  }

  function currentRichTextFromElement(styleOverride = richText.style): RichTextValue {
    return {
      type: 'richText',
      content: ref.current ? parseEditableElement(ref.current) : richText.content,
      style: normalizeRichTextStyle(styleOverride),
    };
  }

  function updateBlockStyle(nextStyle: RichTextValue['style']) {
    if (!richTextEnabled) return;
    const nextRich = currentRichTextFromElement(nextStyle);
    setTextValue(toStoredTextValue(nextRich, nextRich));
    window.requestAnimationFrame(updateSelectionGeometry);
  }

  function applyTextCommand(command: 'bold' | 'italic' | 'underline') {
    if (!richTextEnabled) return;
    const el = ref.current;
    if (!el) return;
    restoreSelection(true);
    document.execCommand(command, false);
    rememberSelection();
    commitFromElement(true);
    window.requestAnimationFrame(updateSelectionGeometry);
  }

  function applyFont(fontFamily: RichTextFont) {
    if (!richTextEnabled) return;
    updateBlockStyle({
      ...richText.style,
      fontFamily: fontFamily === 'inherit' ? undefined : fontFamily,
    });
  }

  function applyFontSize(fontSize: number) {
    updateBlockStyle({
      ...richText.style,
      fontSize: normalizeFontSize(fontSize),
    });
  }

  function applyAlign(textAlign: RichTextAlign) {
    updateBlockStyle({
      ...richText.style,
      textAlign,
    });
  }

  function applyColor(color: string) {
    updateBlockStyle({
      ...richText.style,
      color,
    });
  }

  function rememberSelection() {
    const el = ref.current;
    if (!el) return;
    const selection = document.getSelection();
    if (!isSelectionInside(selection, el)) return;
    const range = selection?.rangeCount ? selection.getRangeAt(0) : null;
    selectionRangeRef.current = range ? range.cloneRange() : null;
    selectionSnapshotRef.current = range ? snapshotSelectionRange(el, range) : null;
  }

  function selectWholeEditor() {
    const el = ref.current;
    if (!el) return;
    el.focus();
    const range = selectElementContents(el);
    selectionRangeRef.current = range;
    selectionSnapshotRef.current = range ? snapshotSelectionRange(el, range) : null;
    window.requestAnimationFrame(updateSelectionGeometry);
  }

  function restoreSelection(selectWholeBlockIfCollapsed = false) {
    const el = ref.current;
    if (!el) return;
    el.focus();
    const selection = document.getSelection();
    if (!selection) return;
    selection.removeAllRanges();
    const storedRange = selectionRangeRef.current;
    if (storedRange && isRangeInside(storedRange, el)) {
      selection.addRange(storedRange);
    } else if (selectionSnapshotRef.current) {
      const range = rangeFromSelectionSnapshot(el, selectionSnapshotRef.current);
      selection.addRange(range);
      selectionRangeRef.current = range.cloneRange();
    }
    if (selectWholeBlockIfCollapsed && (
      selection.rangeCount === 0 ||
      !isSelectionInside(selection, el) ||
      selection.isCollapsed
    )) {
      const range = document.createRange();
      range.selectNodeContents(el);
      selection.removeAllRanges();
      selection.addRange(range);
      selectionRangeRef.current = range.cloneRange();
      selectionSnapshotRef.current = snapshotSelectionRange(el, range);
    }
  }

  function getActiveLinkHref() {
    const el = ref.current;
    if (!el) return '';
    const selection = document.getSelection();
    const anchor = closestAnchor(selection?.anchorNode ?? null, el);
    return anchor?.getAttribute('href') ?? '';
  }

  function getSelectedLinkText() {
    const el = ref.current;
    if (!el) return '';
    restoreSelection(false);
    const selection = document.getSelection();
    if (!selection || selection.rangeCount === 0 || !isSelectionInside(selection, el) || selection.isCollapsed) {
      return '';
    }
    return selection.toString();
  }

  function openLinkEditor() {
    rememberSelection();
    setLinkText(getSelectedLinkText());
    setLinkHref(getActiveLinkHref());
    setLinkEditorOpen((open) => !open);
  }

  function applyLink(nextHref = linkHref, nextText = linkText) {
    if (!richTextEnabled) return;
    const el = ref.current;
    if (!el) return;
    const href = normalizeHref(nextHref);
    const textForLink = nextText.trim();
    const currentRich = currentRichTextFromElement();
    const selection =
      selectionSnapshotRef.current ?? {
        start: plainTextFromSegments(currentRich.content).length,
        end: plainTextFromSegments(currentRich.content).length,
      };
    const hasSelection = selection.start !== selection.end;

    if (!href) {
      if (!hasSelection) {
        setLinkEditorOpen(false);
        window.requestAnimationFrame(updateSelectionGeometry);
        return;
      }
    } else if (!textForLink && !hasSelection) {
      setLinkEditorOpen(false);
      window.requestAnimationFrame(updateSelectionGeometry);
      return;
    }

    const nextRich: RichTextValue = {
      type: 'richText',
      content: applyLinkToSegments(currentRich.content, selection, href, textForLink || undefined),
      style: currentRich.style,
    };
    setTextValue(toStoredTextValue(nextRich, href ? nextRich : resolved));
    setLinkEditorOpen(false);
    selectionSnapshotRef.current = null;
    selectionRangeRef.current = null;
    window.requestAnimationFrame(updateSelectionGeometry);
  }

  function applyList(kind: 'bullet' | 'numbered') {
    if (!richTextEnabled) return;
    const el = ref.current;
    if (!el) return;
    const currentRich = currentRichTextFromElement();
    const contentLength = plainTextFromSegments(currentRich.content).length;
    const selection =
      selectionSnapshotRef.current ?? {
        start: contentLength,
        end: contentLength,
      };
    const nextRich: RichTextValue = {
      type: 'richText',
      content: applyListToSegments(currentRich.content, selection, kind),
      style: currentRich.style,
    };
    setTextValue(toStoredTextValue(nextRich, nextRich));
    selectionSnapshotRef.current = null;
    selectionRangeRef.current = null;
    window.requestAnimationFrame(updateSelectionGeometry);
  }

  function clearInlineFormatting() {
    if (!richTextEnabled) return;
    restoreSelection(true);
    document.execCommand('removeFormat', false);
    document.execCommand('unlink', false);
    commitFromElement(true);
    window.requestAnimationFrame(updateSelectionGeometry);
  }

  function deleteBlock() {
    setTextValue('');
    setSelected(null);
  }

  const computedStyle =
    typeof window !== 'undefined' && ref.current ? window.getComputedStyle(ref.current) : null;
  const toolbarFont: RichTextFont =
    richText.style?.fontFamily ?? detectFontFromComputedStyle(computedStyle?.fontFamily);
  const toolbarFontSize =
    normalizeFontSize(richText.style?.fontSize) ??
    normalizeFontSize(computedStyle ? Number.parseFloat(computedStyle.fontSize) : undefined) ??
    48;
  const toolbarColor =
    normalizeColor(richText.style?.color) ?? rgbToHex(computedStyle?.color) ?? DEFAULT_TEXT_COLOR;
  const toolbarAlign = richText.style?.textAlign ?? 'left';

  const editor = createElement(as, {
    ref,
    className: editClass,
    style: effectiveStyle,
    contentEditable: true,
    suppressContentEditableWarning: true,
    'data-locusly-edit-path': path,
    'data-placeholder': placeholder ?? '',
    onMouseUp: rememberSelection,
    onKeyUp: rememberSelection,
    onFocus: () => {
      if (selectedPath !== path) setSelected(path);
      window.requestAnimationFrame(updateSelectionGeometry);
    },
    onInput: () => {
      commitFromElement();
    },
    onBlur: (e: FocusEvent<HTMLElement>) => {
      if (!richTextEnabled) {
        const next = e.currentTarget.innerText;
        if (next !== text) setTextValue(next);
        return;
      }
      const nextRich: RichTextValue = {
        type: 'richText',
        content: parseEditableElement(e.currentTarget),
        style: richText.style,
      };
      const nextStored = toStoredTextValue(nextRich, resolved);
      const nextText = typeof nextStored === 'string' ? nextStored : plainTextFromSegments(nextStored.content);
      if (nextText !== text || isRichTextValue(nextStored) || isRichTextValue(resolved)) {
        setTextValue(nextStored);
      }
    },
    onKeyDown: (e: KeyboardEvent<HTMLElement>) => {
      const primaryShortcut = (e.ctrlKey || e.metaKey) && !e.altKey;
      const shortcutKey = e.key.toLowerCase();

      if (primaryShortcut && (shortcutKey === 'z' || shortcutKey === 'y')) {
        const direction = shortcutKey === 'y' || e.shiftKey ? 'redo' : 'undo';
        if (applyHistory(direction)) {
          e.preventDefault();
          return;
        }
      }
      if (primaryShortcut && shortcutKey === 'a') {
        e.preventDefault();
        selectWholeEditor();
      }
      if (primaryShortcut && shortcutKey === 'k') {
        if (!richTextEnabled) return;
        e.preventDefault();
        rememberSelection();
        openLinkEditor();
      }
      if (!multiline && e.key === 'Enter') {
        e.preventDefault();
        (e.currentTarget as HTMLElement).blur();
      }
      if (primaryShortcut && shortcutKey === 'b') {
        if (!richTextEnabled) return;
        e.preventDefault();
        applyTextCommand('bold');
      }
      if (primaryShortcut && shortcutKey === 'i') {
        if (!richTextEnabled) return;
        e.preventDefault();
        applyTextCommand('italic');
      }
      if (primaryShortcut && shortcutKey === 'u') {
        if (!richTextEnabled) return;
        e.preventDefault();
        applyTextCommand('underline');
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        (e.currentTarget as HTMLElement).blur();
      }
    },
    onPaste: (e: ClipboardEvent<HTMLElement>) => {
      e.preventDefault();
      const plain = normalizePastedText(e.clipboardData.getData('text/plain'), multiline);
      if (!plain) return;
      if (!insertPlainTextAtSelection(e.currentTarget, plain)) {
        document.execCommand('insertText', false, plain);
      }
      rememberSelection();
      commitFromElement();
      window.requestAnimationFrame(updateSelectionGeometry);
    },
  });

  return (
    <Fragment>
      {richTextEnabled ? <style>{RICH_TEXT_LINK_CSS}</style> : null}
      {isSelected && richTextEnabled && geometry && typeof document !== 'undefined'
        ? createPortal(
            <Fragment>
              <div
                aria-hidden
                style={{
                  ...geometry.frame,
                  zIndex: 2147483000,
                  border: '1px solid rgba(122, 174, 255, 0.9)',
                  borderRadius: 7,
                  boxShadow: '0 0 0 1px rgba(255, 255, 255, 0.55), 0 14px 35px rgba(13, 26, 46, 0.14)',
                  pointerEvents: 'none',
                }}
              >
                {selectionHandlePositions.map((position, index) => (
                  <span
                    key={index}
                    style={{
                      position: 'absolute',
                      ...position,
                      width: 9,
                      height: 9,
                      borderRadius: 999,
                      border: '1px solid rgba(78, 142, 246, 0.95)',
                      background: '#fffaf0',
                      boxShadow: '0 4px 10px rgba(13, 26, 46, 0.18)',
                    }}
                  />
                ))}
              </div>

              <div
                role="toolbar"
                aria-label="Formatacao de texto"
                style={{
                  ...geometry.toolbar,
                  zIndex: 2147483001,
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  gap: 4,
                  maxWidth: 'calc(100vw - 16px)',
                  maxHeight: 'calc(100vh - 16px)',
                  overflowX: 'hidden',
                  overflowY: 'auto',
                  border: '1px solid rgba(255, 255, 255, 0.13)',
                  borderRadius: 12,
                  background: 'linear-gradient(180deg, #242b35 0%, #141922 100%)',
                  boxShadow: '0 24px 70px rgba(6, 12, 20, 0.34), 0 2px 8px rgba(6, 12, 20, 0.22)',
                  color: '#fff8e8',
                  padding: 6,
                  pointerEvents: 'auto',
                  fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                }}
              >
                <span
                  aria-hidden
                  style={{
                    ...geometry.arrow,
                    width: 10,
                    height: 10,
                    background: '#171d26',
                    borderRight: '1px solid rgba(255, 255, 255, 0.13)',
                    borderBottom: '1px solid rgba(255, 255, 255, 0.13)',
                  }}
                />
                <span style={{ display: 'inline-flex', alignItems: 'center', padding: '0 7px', fontSize: 18 }}>
                  T
                </span>
                <select
                  value={toolbarFont}
                  onChange={(event) => applyFont(event.target.value as RichTextFont)}
                  aria-label="Tipo de fonte"
                  style={{
                    height: 32,
                    minWidth: 120,
                    border: 0,
                    borderRadius: 8,
                    background: 'rgba(255, 255, 255, 0.08)',
                    color: '#fff8e8',
                    padding: '0 9px',
                    fontSize: 12,
                    outline: 'none',
                  }}
                >
                  {FONT_OPTIONS.map((option) => (
                    <option
                      key={option.value}
                      value={option.value}
                      style={{ background: '#fffaf0', color: '#1f2933' }}
                    >
                      {option.label}
                    </option>
                  ))}
                </select>
                <input
                  type="number"
                  min={MIN_FONT_SIZE}
                  max={MAX_FONT_SIZE}
                  value={toolbarFontSize}
                  onChange={(event) => applyFontSize(Number(event.target.value))}
                  aria-label="Tamanho do texto"
                  style={{
                    height: 32,
                    width: 56,
                    border: 0,
                    borderRadius: 8,
                    background: 'rgba(255, 255, 255, 0.08)',
                    color: '#fff8e8',
                    padding: '0 7px',
                    fontSize: 12,
                    outline: 'none',
                  }}
                />
                <button
                  type="button"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => applyTextCommand('bold')}
                  aria-label="Bold"
                  style={toolbarButtonStyle('bold')}
                >
                  B
                </button>
                <button
                  type="button"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => applyTextCommand('italic')}
                  aria-label="Italic"
                  style={toolbarButtonStyle('italic')}
                >
                  I
                </button>
                <button
                  type="button"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => applyTextCommand('underline')}
                  aria-label="Sublinhar"
                  style={{ ...toolbarButtonStyle(), textDecorationLine: 'underline' }}
                >
                  U
                </button>
                <span aria-hidden style={toolbarDividerStyle} />
                {ALIGN_OPTIONS.map((align) => (
                  <button
                    key={align}
                    type="button"
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => applyAlign(align)}
                    aria-label={`Alinhar ${align}`}
                    style={toolbarButtonStyle(undefined, toolbarAlign === align)}
                  >
                    {align === 'left' ? '≡' : align === 'center' ? '☰' : '≣'}
                  </button>
                ))}
                <button
                  type="button"
                  onMouseDown={(event) => {
                    event.preventDefault();
                    rememberSelection();
                  }}
                  onClick={openLinkEditor}
                  aria-label="Link"
                  style={toolbarButtonStyle(undefined, linkEditorOpen)}
                >
                  ↗
                </button>
                {linkEditorOpen ? (
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      flexWrap: 'wrap',
                      gap: 4,
                      minHeight: 32,
                      width: 374,
                      maxWidth: '100%',
                      borderRadius: 9,
                      background: 'rgba(255, 255, 255, 0.08)',
                      padding: 3,
                    }}
                  >
                    <input
                      type="text"
                      value={linkText}
                      onChange={(event) => setLinkText(event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                          event.preventDefault();
                          applyLink();
                        }
                        if (event.key === 'Escape') {
                          event.preventDefault();
                          setLinkEditorOpen(false);
                        }
                      }}
                      placeholder="Texto"
                      aria-label="Texto do link"
                      style={{
                        height: 26,
                        width: 126,
                        flex: '1 1 126px',
                        border: 0,
                        borderRadius: 7,
                        background: '#fffaf0',
                        color: '#1f2933',
                        padding: '0 8px',
                        fontSize: 12,
                        outline: 'none',
                      }}
                    />
                    <input
                      type="url"
                      value={linkHref}
                      onChange={(event) => setLinkHref(event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                          event.preventDefault();
                          applyLink();
                        }
                        if (event.key === 'Escape') {
                          event.preventDefault();
                          setLinkEditorOpen(false);
                        }
                      }}
                      placeholder="https://..."
                      aria-label="URL do link"
                      style={{
                        height: 26,
                        width: 154,
                        flex: '1 1 154px',
                        border: 0,
                        borderRadius: 7,
                        background: '#fffaf0',
                        color: '#1f2933',
                        padding: '0 8px',
                        fontSize: 12,
                        outline: 'none',
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => applyLink()}
                      aria-label="Aplicar link"
                      style={{
                        ...toolbarButtonStyle(undefined, true),
                        width: 34,
                        height: 26,
                        flexBasis: 34,
                        fontSize: 11,
                      }}
                    >
                      OK
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setLinkHref('');
                        applyLink('');
                      }}
                      aria-label="Remover link"
                      style={{
                        ...toolbarButtonStyle(),
                        width: 28,
                        height: 26,
                        flexBasis: 28,
                      }}
                    >
                      X
                    </button>
                  </span>
                ) : null}
                <button
                  type="button"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => applyList('bullet')}
                  aria-label="Lista com marcadores"
                  style={toolbarButtonStyle()}
                >
                  {'\u2022'}
                </button>
                <button
                  type="button"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => applyList('numbered')}
                  aria-label="Lista numerada"
                  style={{ ...toolbarButtonStyle(), fontSize: 11 }}
                >
                  1.
                </button>
                <label
                  aria-label="Cor do texto"
                  style={{
                    position: 'relative',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 32,
                    height: 32,
                    borderRadius: 8,
                    background: 'rgba(255, 255, 255, 0.08)',
                    cursor: 'pointer',
                  }}
                >
                  <span
                    aria-hidden
                    style={{
                      width: 15,
                      height: 15,
                      borderRadius: 999,
                      background: toolbarColor,
                      border: '1px solid rgba(255, 255, 255, 0.72)',
                    }}
                  />
                  <input
                    type="color"
                    value={toolbarColor}
                    onChange={(event) => applyColor(event.target.value)}
                    style={{
                      position: 'absolute',
                      inset: 0,
                      opacity: 0,
                      cursor: 'pointer',
                    }}
                  />
                </label>
                <button
                  type="button"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={clearInlineFormatting}
                  aria-label="Limpar formatação"
                  style={{
                    ...toolbarButtonStyle(),
                    width: 44,
                    flexBasis: 44,
                    fontSize: 11,
                  }}
                >
                  Tx
                </button>
                <button
                  type="button"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={deleteBlock}
                  aria-label="Apagar bloco"
                  style={{
                    ...toolbarButtonStyle(),
                    color: '#ffe2d8',
                  }}
                >
                  ⌫
                </button>
              </div>
            </Fragment>,
            document.body,
          )
        : null}
      {editor}
    </Fragment>
  );
}
